/**
 * PlanningToolsPage - What-if planning scenarios.
 *
 * Route: /planning
 *
 * Displays:
 *   - List of existing planning scenarios grouped by status
 *   - "Create New Plan" button to start setup wizard
 *   - Each plan card shows: name, community, analysis type, key params, last updated
 *   - Actions: View Results, Fork, Delete
 */

import React, { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Container, Row, Col, Card, Button, Badge, Form, Spinner, Alert,
  Modal, InputGroup,
} from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useScenarios, useForkScenario, useDeleteScenario } from '../hooks/useScenarios'
import { useCommunities } from '../hooks/useCommunities'
import { resultsAPI } from '../api/results'
import PlanningResultModal from '../components/PlanningResultModal'
import { useAuth } from '../context/AuthContext'

const analysisTypeBadgeColors = {
  cba: { bg: '#E8F5E9', color: '#2E7D32' },
  long_term: { bg: '#E3F2FD', color: '#1565C0' },
  optimization: { bg: '#FFF3E0', color: '#E65100' },
}

function PlanningToolsPage() {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const { t } = useTranslation()
  const analysisTypeLabels = {
    cba: t('planning_tools.type_cba'),
    long_term: t('planning_tools.type_long_term'),
    optimization: t('planning_tools.type_optimization'),
  }
  const [statusFilter, setStatusFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showForkModal, setShowForkModal] = useState(false)
  const [forkTarget, setForkTarget] = useState(null)
  const [forkName, setForkName] = useState('')
  const [savedResultsTarget, setSavedResultsTarget] = useState(null)
  // (checkingResults removed — modal opens synchronously now)

  // Fetch all scenarios and communities
  const { data: scenarios = [], isLoading, isError, error } = useScenarios(
    statusFilter ? { status: statusFilter } : {}
  )
  const { data: communities = [] } = useCommunities()
  const forkScenario = useForkScenario()
  const deleteScenario = useDeleteScenario()

  // Build community name lookup
  const communityMap = {}
  communities.forEach(c => { communityMap[c.id] = c.name })

  // Filter scenarios by search
  const filteredScenarios = scenarios.filter(s => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      s.name?.toLowerCase().includes(term) ||
      communityMap[s.community_id]?.toLowerCase().includes(term)
    )
  })

  // Group by status for display
  const activeScenarios = filteredScenarios.filter(s => s.status !== 'archived')
  const archivedScenarios = filteredScenarios.filter(s => s.status === 'archived')

  /** Handle scenario fork */
  const handleFork = (scenario) => {
    setForkTarget(scenario)
    setForkName(t('planning_tools.fork_copy_suffix', { name: scenario.name }))
    setShowForkModal(true)
  }

  const confirmFork = async () => {
    if (!forkTarget || !forkName) return
    try {
      const forked = await forkScenario.mutateAsync({
        scenarioId: forkTarget.id,
        newName: forkName,
      })
      setShowForkModal(false)
      setForkTarget(null)
      // Navigate to setup page so user can edit the forked scenario
      const forkedId = forked?.id || forked?.data?.id
      if (forkedId) {
        navigate(`/planning/${forkedId}/edit`)
      }
    } catch (err) {
      console.error('Fork failed:', err)
    }
  }

  /** Get analysis type from scenario metadata */
  const getAnalysisType = (scenario) => {
    return scenario.metadata?.analysis_type || 'optimization'
  }

  /** Handle Results button: always open modal showing saved results */
  const handleResultsClick = useCallback((scenario) => {
    setSavedResultsTarget(scenario)
  }, [])

  /** Handle scenario delete */
  const handleDelete = async (scenarioId, scenarioName) => {
    if (!window.confirm(t('planning_tools.delete_confirm', { name: scenarioName }))) return
    try {
      await deleteScenario.mutateAsync({ scenarioId, archiveOnly: false })
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  /** Render a scenario card */
  const renderScenarioCard = (scenario) => {
    const communityName = communityMap[scenario.community_id] || t('planning_tools.unknown_community')
    const updatedAt = scenario.updated_at
      ? new Date(scenario.updated_at).toLocaleDateString()
      : '--'
    const analysisType = getAnalysisType(scenario)
    const typeBadge = analysisTypeBadgeColors[analysisType] || analysisTypeBadgeColors.optimization
    const typeLabel = analysisTypeLabels[analysisType] || t('planning_tools.type_default')

    // Extract key params from metadata based on analysis type
    const meta = scenario.metadata || {}
    const cbaP = meta.cbaParams || {}

    return (
      <Col key={scenario.id} xs={12} md={6} xl={4}>
        <div className="planning-card h-100">
          <div className="planning-card-header">
            <div>
              <h6 style={{ margin: 0, fontWeight: 600, color: 'var(--color-neutral-900)' }}>
                {scenario.name}
              </h6>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 'var(--space-1)' }}>
                {communityName}
              </div>
            </div>
            <Badge
              bg="none"
              style={{ backgroundColor: typeBadge.bg, color: typeBadge.color, fontSize: '0.65rem', fontWeight: 600 }}
            >
              {typeLabel}
            </Badge>
          </div>

          <div className="planning-card-body">
            <div className="d-flex flex-column gap-2" style={{ fontSize: 'var(--font-size-sm)' }}>
              {analysisType === 'cba' && (
                <>
                  <div className="d-flex justify-content-between">
                    <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_period')}</span>
                    <span style={{ fontWeight: 500 }}>{t('planning_tools.card_period_value', { count: cbaP.analysisPeriodYears || 20 })}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_new_pv_battery')}</span>
                    <span style={{ fontWeight: 500 }}>
                      {cbaP.newPvCapacityKwp || 0} kWp / {cbaP.newBatteryCapacityKwh || 0} kWh
                    </span>
                  </div>
                </>
              )}
              {analysisType === 'long_term' && (
                <div className="d-flex justify-content-between">
                  <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_horizon')}</span>
                  <span style={{ fontWeight: 500 }}>
                    {meta.longTermParams?.timeHorizon?.replace('_', ' ') || t('planning_tools.card_horizon_default')}
                  </span>
                </div>
              )}
              {analysisType === 'optimization' && (
                <>
                  <div className="d-flex justify-content-between">
                    <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_objective')}</span>
                    <span style={{ fontWeight: 500 }}>
                      {scenario.objective === 'min_cost' ? t('optimization_params.obj_min_cost_short') :
                        scenario.objective === 'max_self_sufficiency' ? t('optimization_params.obj_max_ss_short') :
                          scenario.objective === 'max_self_consumption' ? t('optimization_params.obj_max_sc_short') :
                            scenario.objective === 'min_co2' ? t('optimization_params.obj_min_co2_short') :
                              scenario.objective || '--'}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_horizon')}</span>
                    <span style={{ fontWeight: 500 }}>
                      {scenario.horizon_days === 1 ? t('planning_tools.card_dayahead') :
                        scenario.horizon_days === 7 ? t('planning_tools.card_weekly') :
                          t('planning_tools.card_horizon_days', { count: scenario.horizon_days || 1 })}
                    </span>
                  </div>
                </>
              )}
              <div className="d-flex justify-content-between">
                <span style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.card_last_updated')}</span>
                <span style={{ fontWeight: 500 }}>{updatedAt}</span>
              </div>
            </div>
          </div>

          <div className="planning-card-footer">
            <Button
              size="sm"
              variant="primary"
              style={{ fontSize: 'var(--font-size-xs)', flex: 1, backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
              onClick={() => handleResultsClick(scenario)}
            >
              {t('planning_tools.btn_results')}
            </Button>
            {canEdit && (
              <Button
                size="sm"
                variant="outline-secondary"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={() => handleFork(scenario)}
              >
                {t('planning_tools.btn_fork')}
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                variant="outline-danger"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={() => handleDelete(scenario.id, scenario.name)}
              >
                {t('planning_tools.btn_delete')}
              </Button>
            )}
          </div>
        </div>
      </Col>
    )
  }

  return (
    <Container>
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h1 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)' }}>
                {t('planning_tools.header')}
              </h1>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 0 }}>
                {t('planning_tools.subheader')}
              </p>
            </div>
            {canEdit && (
              <Button
                as={Link}
                to="/planning/new"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  borderColor: 'var(--color-primary)',
                  fontWeight: 600,
                  padding: 'var(--space-3) var(--space-6)',
                }}
              >
                {t('planning_tools.btn_create')}
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Filters Bar */}
      <Row className="mb-4 g-3">
        <Col md={6} lg={4}>
          <InputGroup>
            <InputGroup.Text style={{ backgroundColor: 'var(--color-neutral-50)' }}>
              &#x1F50D;
            </InputGroup.Text>
            <Form.Control
              placeholder={t('planning_tools.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3} lg={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t('planning_tools.filter_all')}</option>
            <option value="draft">{t('planning_tools.filter_draft')}</option>
            <option value="published">{t('planning_tools.filter_published')}</option>
            <option value="archived">{t('planning_tools.filter_archived')}</option>
          </Form.Select>
        </Col>
        <Col md={3} lg={2}>
          <span style={{ lineHeight: '38px', fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)' }}>
            {filteredScenarios.length === 1
              ? t('planning_tools.plans_found_one', { count: 1 })
              : t('planning_tools.plans_found_other', { count: filteredScenarios.length })}
          </span>
        </Col>
      </Row>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
          <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>{t('planning_tools.loading_plans')}</p>
        </div>
      )}

      {/* Error */}
      {isError && (
        <Alert variant="danger">
          {t('planning_tools.load_failed', { error: error?.message || t('planning_tools.unknown_error') })}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && !isError && filteredScenarios.length === 0 && (
        <Row className="justify-content-center py-5">
          <Col md={8} lg={6} className="text-center">
            <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)', opacity: 0.3 }}>
              &#x1F9EA;
            </div>
            <h4 style={{ color: 'var(--color-neutral-800)' }}>{t('planning_tools.empty_title')}</h4>
            <p style={{ color: 'var(--color-neutral-600)' }}>
              {t('planning_tools.empty_text')}
            </p>
            {canEdit && (
              <Button
                as={Link}
                to="/planning/new"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  borderColor: 'var(--color-primary)',
                  fontWeight: 600,
                }}
              >
                {t('planning_tools.empty_btn')}
              </Button>
            )}
          </Col>
        </Row>
      )}

      {/* Active Scenarios */}
      {activeScenarios.length > 0 && (
        <>
          <h5 style={{
            color: 'var(--color-neutral-800)',
            fontWeight: 600,
            marginBottom: 'var(--space-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}>
            {t('planning_tools.section_active')}
            <Badge bg="secondary" style={{ fontWeight: 500, fontSize: '0.7rem' }}>
              {activeScenarios.length}
            </Badge>
          </h5>
          <Row className="g-3 mb-5">
            {activeScenarios.map(renderScenarioCard)}
          </Row>
        </>
      )}

      {/* Archived Scenarios */}
      {archivedScenarios.length > 0 && (
        <>
          <h5 style={{
            color: 'var(--color-neutral-500)',
            fontWeight: 600,
            marginBottom: 'var(--space-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}>
            {t('planning_tools.section_archived')}
            <Badge bg="secondary" style={{ fontWeight: 500, fontSize: '0.7rem' }}>
              {archivedScenarios.length}
            </Badge>
          </h5>
          <Row className="g-3 mb-5">
            {archivedScenarios.map(renderScenarioCard)}
          </Row>
        </>
      )}

      {/* Saved Results Modal */}
      <PlanningResultModal
        show={!!savedResultsTarget}
        onHide={() => setSavedResultsTarget(null)}
        scenarioId={savedResultsTarget?.id}
        communityId={savedResultsTarget?.community_id}
        scenarioName={savedResultsTarget?.name}
        currency={communities.find(c => c.id === savedResultsTarget?.community_id)?.currency || 'EUR'}
      />

      {/* Fork Modal */}
      <Modal show={showForkModal} onHide={() => setShowForkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 'var(--font-size-lg)' }}>
            {t('planning_tools.fork_title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)' }}>
            {t('planning_tools.fork_intro', { name: forkTarget?.name || '' })}
          </p>
          <Form.Group>
            <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
              {t('planning_tools.fork_new_name')}
            </Form.Label>
            <Form.Control
              value={forkName}
              onChange={(e) => setForkName(e.target.value)}
              placeholder={t('planning_tools.fork_placeholder')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowForkModal(false)}>
            {t('planning_tools.btn_cancel')}
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
            onClick={confirmFork}
            disabled={!forkName.trim() || forkScenario.isPending}
          >
            {forkScenario.isPending ? t('planning_tools.btn_forking') : t('planning_tools.btn_create_fork')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default PlanningToolsPage
